import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import { F0Box, F0Button, F0Form, F0Select } from '@factorialco/f0-react'

import type { F0FormRef } from '@factorialco/f0-react'
import type {
  CostCenterSelectorV2Query,
  FinancialProjectSelectorV2Query,
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'
import {
  CostCenterSelectorV2Document,
  FinancialProjectSelectorV2Document,
  GetCurrencySelectorCurrenciesDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'
import styles from './index.module.css'
import { useLegalEntitySelectorSource } from 'modules/core/lib/formFields/legalEntitySelector/source'
import { useTeamSelectorSource } from 'modules/core/lib/formFields/teamSelector/source'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

import type { EditCallbacks } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail'
import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'

import EditLineItemsModal from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/DetailsTab/EditLineItemsModal'
import LineItemsSection from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/DetailsTab/LineItemsSection'
import VendorField from './VendorField'
import type { EmployeeRenderFn, CurrencyRenderFn } from './form'
import { usePurchaseOrderEditForm } from './form'

interface Props {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  companyId: string
  locale: string
  lineItemsRef: MutableRefObject<LineItemDraft[]>
  lineItems: LineItemDraft[]
  formRef: MutableRefObject<F0FormRef | null>
  editCallbacks: EditCallbacks
  onSuccess: () => void
  targetStatus?: ProcurementValueObjectsPurchaseOrderStatusEnum
}

function PurchaseOrderEditForm({
  purchaseOrder,
  companyId,
  locale,
  lineItemsRef,
  lineItems,
  formRef,
  editCallbacks,
  onSuccess,
  targetStatus,
}: Props) {
  const [lineItemsModalOpen, setLineItemsModalOpen] = useState(false)

  // Resolve the initial currency from the EAV value (if set) or the PO cost currency
  const initialCurrency = useMemo(() => {
    const definitions = purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? []
    const currencyDef = definitions.find((d) => d.fieldKey === 'currency')
    if (currencyDef) {
      const fv = purchaseOrder.poFieldValues.find((v) => v.fieldDefinition?.id === currencyDef.id)
      if (fv?.value) return fv.value
    }
    return purchaseOrder.cost.currency
  }, [purchaseOrder])

  const [currentCurrency, setCurrentCurrency] = useState(initialCurrency)
  const [currentLegalEntityId, setCurrentLegalEntityId] = useState(purchaseOrder.legalEntity.id)

  const teamSource = useTeamSelectorSource()
  const legalEntitySource = useLegalEntitySelectorSource()

  // Keep stable references to data sources so the renders object
  // (and therefore F0Form schema) doesn't change on every re-render.
  // The sources remain semantically equivalent across renders.
  const teamSourceRef = useRef(teamSource)
  teamSourceRef.current = teamSource
  // Legal entity render: wraps F0Select with source + side-effect to track current legal entity
  const legalEntitySourceRef = useRef(legalEntitySource)
  legalEntitySourceRef.current = legalEntitySource

  const legalEntityRender = useCallback(
    ({
      value,
      onChange,
      label,
      required,
    }: {
      value: string
      onChange: (v: string) => void
      label: string
      required: boolean
    }) => (
      <F0Select
        label={label}
        value={value}
        onChange={(v: string | null) => {
          if (v !== null) {
            onChange(v)
            setCurrentLegalEntityId(v)
          }
        }}
        source={legalEntitySourceRef.current}
        mapOptions={(le: { id: string; legalName: string }) => ({
          value: le.id,
          label: le.legalName,
          item: le,
        })}
        required={required}
        showSearchBox
        size="md"
      />
    ),
    []
  )

  const { data: currencyData } = useQuery(GetCurrencySelectorCurrenciesDocument)
  const currencyOptions = useMemo(() => {
    const edges = currencyData?.apiCore?.currenciesConnection?.edges ?? []
    return edges
      .map((edge) => edge?.node)
      .filter(Boolean)
      .map((node) => ({ value: node.isoCode, label: node.isoCode }))
  }, [currencyData])

  const allDefinitions = useMemo(
    () => purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? [],
    [purchaseOrder.poTemplateVersion]
  )

  const columnDefinitions = useMemo(() => {
    return [...allDefinitions]
      .filter((d) => d.sectionType === 'line_item_columns' && d.visible)
      .sort((a, b) => a.position - b.position)
  }, [allDefinitions])

  // Find the form field IDs for vendor computed fields so VendorField can auto-populate them
  const vendorIdNumberFieldId = useMemo(
    () => allDefinitions.find((d) => d.fieldKey === 'vendor_id_number')?.id ?? null,
    [allDefinitions]
  )
  const addressFieldId = useMemo(
    () => allDefinitions.find((d) => d.fieldKey === 'address')?.id ?? null,
    [allDefinitions]
  )
  const vendorEmailFieldId = useMemo(
    () => allDefinitions.find((d) => d.fieldKey === 'vendor_email')?.id ?? null,
    [allDefinitions]
  )
  const vendorLedgerAccountFieldId = useMemo(
    () => allDefinitions.find((d) => d.fieldKey === 'vendor_ledger_account')?.id ?? null,
    [allDefinitions]
  )

  // Resolve the PO legal entity ID for vendor ledger account lookup.
  // Uses form-local state so that changing legal entity in the form
  // immediately recalculates the vendor ledger account.
  const legalEntityId = currentLegalEntityId

  const vendorRender = useCallback(
    ({
      value,
      onChange,
      label,
      required,
    }: {
      value: string
      onChange: (v: string) => void
      label: string
      required: boolean
    }) => (
      <VendorField
        value={value}
        onChange={onChange}
        label={label}
        required={required}
        companyId={companyId}
        formRef={formRef}
        vendorIdNumberFieldId={vendorIdNumberFieldId}
        addressFieldId={addressFieldId}
        vendorEmailFieldId={vendorEmailFieldId}
        vendorLedgerAccountFieldId={vendorLedgerAccountFieldId}
        legalEntityId={legalEntityId}
      />
    ),
    [
      companyId,
      formRef,
      vendorIdNumberFieldId,
      addressFieldId,
      vendorEmailFieldId,
      vendorLedgerAccountFieldId,
      legalEntityId,
    ]
  )

  const employeeRender: EmployeeRenderFn = useCallback(
    ({ value, onChange, label, required, disabled }) => (
      <EmployeeSelectorV2
        label={label}
        value={value}
        onChange={onChange}
        mandatory={required}
        disabled={disabled}
        options={{ hideSearchIcon: true }}
      />
    ),
    []
  )

  // Currency render: F0Select with options + side-effect to track current currency
  const currencyOptionsRef = useRef(currencyOptions)
  currencyOptionsRef.current = currencyOptions

  const currencyRender: CurrencyRenderFn = useCallback(
    ({ value, onChange, label, required }) => (
      <F0Select
        label={label}
        value={value}
        onChange={(v: string | null) => {
          if (v !== null) {
            onChange(v)
            setCurrentCurrency(v)
          }
        }}
        options={currencyOptionsRef.current}
        required={required}
        showSearchBox
        size="md"
      />
    ),
    []
  )

  // Cost center & project data sources for header-level fields (general_information section)
  const costCenterSource = useGraphqlDataSource(
    {
      document: CostCenterSelectorV2Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({ search: search ?? undefined }),
      mapData: (data: CostCenterSelectorV2Query) =>
        data.costCenters?.costCentersConnection?.edges?.map((e) => e.node) ?? [],
      mapPagination: (data: CostCenterSelectorV2Query) => ({
        total: data.costCenters?.costCentersConnection?.totalCount ?? 0,
        cursor: data.costCenters?.costCentersConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.costCenters?.costCentersConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const projectSource = useGraphqlDataSource(
    {
      document: FinancialProjectSelectorV2Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({ search: search ?? undefined }),
      mapData: (data: FinancialProjectSelectorV2Query) =>
        data.projectManagement?.financialProjectsConnection?.edges?.map((e) => e.node) ?? [],
      mapPagination: (data: FinancialProjectSelectorV2Query) => ({
        total: data.projectManagement?.financialProjectsConnection?.totalCount ?? 0,
        cursor:
          data.projectManagement?.financialProjectsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage:
          data.projectManagement?.financialProjectsConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const costCenterSourceRef = useRef(costCenterSource)
  costCenterSourceRef.current = costCenterSource
  const projectSourceRef = useRef(projectSource)
  projectSourceRef.current = projectSource

  // Keep latest line item data accessible via refs so the lineItemsRender
  // callback identity stays stable and doesn't cause F0Form to re-init
  // its schema every time line items change.
  const purchaseOrderRef = useRef(purchaseOrder)
  purchaseOrderRef.current = purchaseOrder
  const lineItemsStateRef = useRef(lineItems)
  lineItemsStateRef.current = lineItems
  const currentCurrencyRef = useRef(currentCurrency)
  currentCurrencyRef.current = currentCurrency

  const lineItemsRender = useCallback(
    () => (
      <F0Box display="flex" flexDirection="column" gap="sm">
        <F0Box display="flex" justifyContent="end">
          <F0Button
            label={i18n.t('procurement.purchase_orders.detail.edit_line_items')}
            variant="outline"
            type="button"
            onClick={() => setLineItemsModalOpen(true)}
          />
        </F0Box>
        <LineItemsSection
          purchaseOrder={purchaseOrderRef.current}
          locale={locale}
          isEditing
          lineItems={lineItemsStateRef.current}
          currency={currentCurrencyRef.current}
        />
      </F0Box>
    ),
    [locale]
  )

  // Stabilize renders to prevent F0Form from re-initializing when
  // unrelated state changes. teamSource, legalEntitySource, and
  // lineItemsRender are accessed via refs so the renders object
  // identity stays stable while always using the latest data when
  // F0Form actually renders the fields.
  const renders = useMemo(
    () => ({
      vendorRender,
      employeeRender,
      get teamSource() {
        return teamSourceRef.current
      },
      legalEntityRender,
      get legalEntitySource() {
        return legalEntitySourceRef.current
      },
      get costCenterSource() {
        return costCenterSourceRef.current
      },
      get projectSource() {
        return projectSourceRef.current
      },
      currencyRender,
      lineItemsRender,
    }),
    [vendorRender, employeeRender, legalEntityRender, currencyRender, lineItemsRender]
  )

  const { schema, sections, defaultValues, onSubmit } = usePurchaseOrderEditForm(
    purchaseOrder,
    companyId,
    lineItemsRef,
    onSuccess,
    renders,
    targetStatus
  )

  return (
    <>
      <F0Form
        formRef={formRef}
        name="purchase-order-edit"
        schema={schema}
        sections={sections}
        defaultValues={defaultValues}
        submitConfig={{ type: 'default', hideSubmitButton: true }}
        onSubmit={onSubmit}
        styling={{ showSectionsSidepanel: false }}
        className={styles.fullWidthForm}
      />
      <EditLineItemsModal
        isOpen={lineItemsModalOpen}
        onClose={() => setLineItemsModalOpen(false)}
        lineItems={lineItemsRef.current}
        columnDefinitions={columnDefinitions}
        allDefinitions={allDefinitions}
        replaceLineItems={editCallbacks.replaceLineItems}
        purchaseOrder={purchaseOrder}
        locale={locale}
        currency={currentCurrency}
      />
    </>
  )
}

export default PurchaseOrderEditForm
